import React from 'react';

// Selector for all focusable menu item types
export const MENU_ITEM_SELECTOR = '[role="menuitemcheckbox"], [role="menuitemradio"], .bs4-dropdown-item, .accordion-header';

// Natural focusable elements
export const NATURALLY_FOCUSABLE_SELECTOR = 'a[href], button, input, select, textarea, [role="button"]';

/**
 * Filters menu items to only those that are truly focusable and visible.
 * 
 * @param items - Array of HTMLElements to filter
 * @returns Filtered array of focusable items
 */
export const filterFocusableMenuItems = (items: HTMLElement[]): HTMLElement[] => {
  return items.filter(item => {
    // Check if item is visible (not in a collapsed accordion)
    const accordionCollapse = item.closest('.accordion-collapse');
    if (accordionCollapse && !accordionCollapse.classList.contains('expanded')) {
      return false; // Skip items in collapsed accordions
    }
    
    // Special case: items inside accordion content are always treated as top-level
    const isInAccordion = !!item.closest('.accordion-content');
    if (isInAccordion) {
      const tabIndex = item.tabIndex;
      return tabIndex >= 0 || item.matches(NATURALLY_FOCUSABLE_SELECTOR);
    }
    
    // For non-accordion items, check if they're nested inside another menu item
    const parent = item.parentElement?.closest(MENU_ITEM_SELECTOR);
    const isNested = parent && parent !== item;
    if (isNested) return false;
    
    const tabIndex = item.tabIndex;
    return tabIndex >= 0 || item.matches(NATURALLY_FOCUSABLE_SELECTOR);
  });
};

/**
 * Custom hook for handling keyboard navigation in dropdown menu items.
 * Provides arrow key navigation (up/down) between focusable menu items.
 *
 * @param itemRef - Reference to the menu item element
 * @returns Handler function for arrow key events
 */
const useMenuItemKeyboardNav = (itemRef: React.RefObject<HTMLElement>) => {
  const handleArrowKeys = (e: React.KeyboardEvent) => {
    const isDown = e.key === 'ArrowDown' || e.key === 'Down';
    const isUp = e.key === 'ArrowUp' || e.key === 'Up';
    const isTab = e.key === 'Tab';

    if (!isDown && !isUp && !isTab) return false;

    const menu = itemRef.current?.closest('.bs4-dropdown-menu');
    if (!menu) return false;

    // Query for menu items with roles, then filter to only truly focusable elements
    const allItems = Array.from(
      menu.querySelectorAll(MENU_ITEM_SELECTOR)
    ) as HTMLElement[];

    const focusableItems = filterFocusableMenuItems(allItems);

    if (focusableItems.length === 0) return false;

    if (isTab) {
      // Tab/Shift+Tab moves between accordion headers only, without closing the menu.
      // stopPropagation blocks react-overlays from intercepting Tab and closing the dropdown.
      const headers = focusableItems.filter(el => el.classList.contains('accordion-header'));
      if (headers.length === 0) return false;

      const isForward = !e.shiftKey;
      const currentFocusIdx = focusableItems.indexOf(itemRef.current!);

      let targetHeader: HTMLElement | undefined;
      if (isForward) {
        targetHeader = headers.find(h => focusableItems.indexOf(h) > currentFocusIdx) ?? headers[0];
      } else {
        const prevHeaders = headers.filter(h => focusableItems.indexOf(h) < currentFocusIdx);
        targetHeader = prevHeaders[prevHeaders.length - 1] ?? headers[headers.length - 1];
      }

      if (!targetHeader) return false;

      e.preventDefault();
      e.stopPropagation();
      targetHeader.focus();
      return true;
    }

    // Arrow key navigation — try each candidate in order and skip any that
    // can't actually receive focus (e.g. <a> elements without href).
    const currentIndex = focusableItems.indexOf(itemRef.current!);
    if (currentIndex === -1) return false;

    const step = isDown ? 1 : -1;
    const len = focusableItems.length;

    e.preventDefault();
    e.stopPropagation();

    // Walk candidates one step at a time (with wrap-around), skipping any
    // that silently reject focus despite passing the focusable-items filter.
    for (let i = 1; i <= len; i++) {
      const idx = ((currentIndex + step * i) % len + len) % len;
      const target = focusableItems[idx];
      if (!target) continue;
      target.focus();
      if (document.activeElement === target) return true;
    }

    return true;
  };

  return handleArrowKeys;
};

export default useMenuItemKeyboardNav;
